/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Construct from 'can/construct/construct';
import can from 'can/util/util';
import Modernizr from 'modernizr';
import appConfig from 'scripts/app-config';

// Detect screen size
const mediumScreenSize = appConfig['screen-sizes'].medium;
const largeScreenSize = appConfig['screen-sizes'].large;
const mq = `(min-width: ${mediumScreenSize.min}px) and (max-width: ${
    largeScreenSize.max
}px)`;

// private variables
var userAgent = window.navigator.userAgent.toLowerCase();

const tabletUrlSegment = '.table';

const isTabletUrl = () => {
    return window.location.href.search(tabletUrlSegment);
};

// private functions
var isAndroid = () => {
    return userAgent.search(/android/) !== -1;
};

var isIos = () => {
    return userAgent.search(/(i(pod|pad|phone))/) !== -1;
};

var isPhoneIos = () => {
    return userAgent.search(/(i(pod|phone))/) !== -1;
};

var isMobile = () => {
    return !isTabletUrl() && (isAndroid() || isIos());
};

var isPhone = () => {
    return !isTabletUrl() && (isAndroid() || isPhoneIos());
};

var isTablet = () => {
    return (
        isTabletUrl() ||
        (Modernizr.touchevents && Modernizr.mediaqueries && Modernizr.mq(mq))
    );
};

var isOldIE = () => {
    var markerPosition = userAgent.indexOf('msie ');

    if (markerPosition > 0) {
        return parseInt(
            userAgent.substring(
                markerPosition + 5,
                userAgent.indexOf('.', markerPosition),
            ),
        );
    }
};

var isModernIE = () => {
    var markerPosition = userAgent.indexOf('trident/');
    var versionMarkerPosition = userAgent.indexOf('rv:');

    if (markerPosition > 0) {
        return parseInt(
            userAgent.substring(
                versionMarkerPosition + 3,
                userAgent.indexOf('.', versionMarkerPosition),
            ),
        );
    }
};

var isEdgeIE = () => {
    var markerPosition = userAgent.indexOf('edge/');

    if (markerPosition > 0) {
        return parseInt(
            userAgent.substring(
                markerPosition + 5,
                userAgent.indexOf('.', markerPosition),
            ),
        );
    }
};

var isIE = (checkVersion) => {
    var version = isOldIE() || isModernIE() || isEdgeIE();

    return checkVersion ? checkVersion == version : version;
};

var isSafari = () => {
    return /^((?!chrome|crios|firefox|fxios|android).)*safari/i.test(userAgent);
};

var isFirefoxOnAndroid = () => {
    return isMobile() && userAgent.indexOf('firefox') !== -1;
};

var isTouch = () => {
    var isAvailable = 'ontouchstart' in window;

    // on some versions and configurations of IE on desktop touch feature is detected not correctly
    if (window.navigator.msPointerEnabled) {
        /* desktop will not have touch points */
        isAvailable = navigator.msMaxTouchPoints > 0;
    }
    return isAvailable;
};

var isNative = () => {
    return can.$('html').hasClass('native');
};

const isMobileWeb = () => {
    return isPhone() && (!isTablet() && !isNative());
};

var canPlayHtml5Hls = () => {
    var video = document.createElement('video');
    var canPlayHls =
        video.canPlayType !== undefined &&
        (video.canPlayType('application/x-mpegURL') === 'probably' ||
            video.canPlayType('application/vnd.apple.mpegURL') === 'probably');
    return isMobile() || canPlayHls;
};

var hasFlashPlugin = () => {
    var hasFlash = false;
    try {
        var flashObject = new ActiveXObject('ShockwaveFlash.ShockwaveFlash');
        if (flashObject) {
            hasFlash = true;
        }
    } catch (e) {
        if (
            navigator.mimeTypes &&
            navigator.mimeTypes['application/x-shockwave-flash'] !==
                undefined &&
            navigator.mimeTypes['application/x-shockwave-flash'].enabledPlugin
        ) {
            hasFlash = true;
        }
    }
    return hasFlash;
};

var getIOSVersion = () => {
    const matched = window.navigator.userAgent.match(
        /\sversion\/([1-9]+\.[0-9]+)\s|\sos\s([1-9]+_[0-9])+\s/i,
    );

    if (!matched || !isPhoneIos()) {
        return undefined;
    }

    if (matched[1]) {
        return Number(matched[1]);
    }

    if (matched[2]) {
        const numericValue = matched[2].replace('_', '.');
        return Number(numericValue);
    }
};

(function disableZoom() {
    if (isIos()) {
        document.addEventListener(
            'touchstart',
            function(event) {
                if (event.touches.length > 1) {
                    event.preventDefault();
                }
            },
            false,
        );
    }
})();

const IOS_DEVICE_LIST = [
    {
        title: 'iPhone 6s Plus',
        innerHeight: 706,
        innerWidth: 414,
    },
    {
        title: 'iPhone 6 Plus',
        innerHeight: 706,
        innerWidth: 414,
    },
    {
        title: 'iPhone 6s',
        innerHeight: 553,
        innerWidth: 414,
    },
    {
        title: 'iPhone 6',
        innerHeight: 553,
        innerWidth: 375,
    },
    {
        title: 'iPhone 7',
        innerHeight: 553,
        innerWidth: 375,
    },
    {
        title: 'iPhone 7 Plus',
        innerHeight: 709,
        innerWidth: 414,
    },
    {
        title: 'iPhone 8',
        innerHeight: 553,
        innerWidth: 375,
    },
    {
        title: 'iPhone 8 Plus',
        innerHeight: 709,
        innerWidth: 414,
    },
    {
        title: 'iPhone SE',
        innerHeight: 454,
        innerWidth: 320,
    },
    {
        title: 'iPhone 4',
        innerHeight: 454,
        innerWidth: 320,
    },
    {
        title: 'iPhone X',
        innerHeight: 719,
        innerWidth: 375,
    },
    {
        title: 'iPad Air',
        innerHeight: 954,
        innerWidth: 768,
    },
    {
        title: 'iPad Air 2',
        innerHeight: 954,
        innerWidth: 768,
    },
    {
        title: 'iPad (5th generation)',
        innerHeight: 954,
        innerWidth: 768,
    },
    {
        title: 'iPad Pro (9.7-inch)',
        innerHeight: 954,
        innerWidth: 768,
    },
    {
        title: 'iPad Pro (12.9-inch)',
        innerHeight: 1296,
        innerWidth: 1024,
    },
    {
        title: 'iPad Pro (10.5-inch)',
        innerHeight: 1024,
        innerWidth: 834,
    },
];

const getIOSBannerOffset = () => {
    const offset = can.$(window).innerHeight() - window.innerHeight;
    return offset > 0 ? offset : 0;
};

function reCheckMobile() {
    userAgent = window.navigator.userAgent.toLowerCase();
    return isMobile();
}

export { reCheckMobile };

export default Construct.extend(
    {
        isIE,
        isSafari,
        isIos,
        getIOSBannerOffset,
        isOldIE: isOldIE(),
        isModernIE: isModernIE(),
        isEdgeIE: isEdgeIE(),
        isMobile: isMobile(),
        isMobileWeb: isMobileWeb(),
        isPhone: isPhone(),
        isTablet: isTablet(),
        isAndroid: isAndroid(),
        isTouch: isTouch(),
        isNative: isNative(),
        canPlayHtml5Hls: canPlayHtml5Hls(),
        hasFlashPlugin: hasFlashPlugin(),
        isFirefoxOnAndroid: isFirefoxOnAndroid(),
        iosVersion: getIOSVersion(),
    },
    {},
);
